import { createSelector } from '@reduxjs/toolkit';
import { assign, compose, isEmpty, map, omit, pick, property } from 'lodash/fp';
import { ShellCompanyDeclaration } from 'utils/sell-process/sell-process-detailed-types';
import { RootState } from '../../../global';
// eslint-disable-next-line max-len
import { sellProcessPeopleSelectors } from '../../../components/admin/sell-process/components/sell-process-people/sell-process-people-redux';
// eslint-disable-next-line max-len
import { sellProcessOrganizationsSelectors } from '../../../components/admin/sell-process/components/sell-process-organizations/sell-process-organizations-redux';
import {
  SellOrganizationOwnerForm,
  SellOrganizationOwnerStore,
  SellOrganizationSignerForm,
  SellOwnerRelation,
  SellPersonOwnerForm,
  SellPersonOwnerStore,
} from '../../../components/admin/sell-process/components/sell-process-form/components/owners-agreements/owners-agreements-types';
import {
  SellRepresentativeRelation,
  SellRepresentativesFormState,
} from '../../../components/admin/sell-process/components/sell-process-form/components/representatives/representatives-types';
import {
  mapOmitEmptyStrings,
  pickShellCompanyDeclaration,
  pickSignerPerson,
} from '../../../components/admin/sell-process/sell-process-utils';
import { omitEmptyStrings } from '../../../utils/omit-empty-strings';
import { renameProperty } from '../../../utils/rename-property';
import { pickCompany } from '../../../components/admin/sell-process/components/sell-process-form/components/basic-info/basic-info-utils';
import {
  SellOrderAuditorStore,
  SellOrderRelationType,
} from '../../../components/admin/sell-process/sell-process-types';
import { CUSTOMER_FORM_ID, NEW_BANK } from '../../../global/constants';
import { isPresent } from '../../../utils/is-present';

export const selectUpdateOwnersAgreementsFactory = (id: string) =>
  createSelector(
    (state: RootState) => {
      const sellOrder = state.sellOrder[id];
      const peopleEntities = sellProcessPeopleSelectors.selectEntities(
        state.sellOrderPeople[id],
      );
      const organizationEntities = sellProcessOrganizationsSelectors.selectEntities(
        state.sellOrderOrganizations[id],
      );

      const {
        physicalPeople,
        legalShareholders,
        contractWording,
        equity,
        purchasePrice,
      } = sellOrder;

      const mergeRelationWithOrganization = (
        relation: SellOwnerRelation<SellOrganizationOwnerStore>,
      ) => {
        const organizationEntity = organizationEntities[relation.organization];
        const signersWithIds = relation.signers
          .map(({ signerId }) => {
            if (!organizationEntity || !signerId) return undefined;

            const signer = organizationEntity.signers.find(
              (signer) => signer.signerId === signerId,
            );

            if (!signer) return undefined;

            return {
              firstName: signer.firstName,
              lastName: signer.lastName,
              signerId: signer.signerId,
              personId: signer.personId,
            };
          })
          .filter(isPresent);
        const signersWithoutIds = organizationEntity?.signers.filter((signer) => !signer.signerId)
          ?? [];

        const signers = [...signersWithIds, ...signersWithoutIds];

        return assign(organizationEntity, {
          ...omit(['organization', 'signers'], relation),
          signers,
        });
      };

      const mergeRelationWithPerson = (
        relation: SellOwnerRelation<SellPersonOwnerStore>,
      ): SellPersonOwnerForm =>
        assign(peopleEntities[relation.person], omit('person', relation));

      const peopleSellers = map(mergeRelationWithPerson, physicalPeople);

      const organizationSellers = map(
        mergeRelationWithOrganization,
        legalShareholders,
      ) as unknown as SellOrganizationOwnerForm[];

      return {
        sellers: [...organizationSellers, ...peopleSellers],
        contractWording,
        equity,
        purchasePrice,
        organizationSellers,
        peopleSellers,
      };
    },
    (state) => state,
  );

const selectUpdateRepresentativesFactory = (id: string) =>
  createSelector(
    (state: RootState) => {
      const { representatives } = state.sellOrder[id];
      const peopleEntities = sellProcessPeopleSelectors.selectEntities(
        state.sellOrderPeople[id],
      );

      const mergePersonWithRelation = (person: SellRepresentativeRelation) => {
        const omitBankProperties = omit(['bankId', 'bankAccount']);
        return assign(
          omitBankProperties(peopleEntities[person.person]),
          omit('person', person),
        );
      };

      return (
        (map(
          mergePersonWithRelation,
          representatives,
        ) as unknown as SellRepresentativesFormState['representatives']) ?? []
      );
    },
    (state) => state,
  );

function getAuditorObject(auditor: SellOrderAuditorStore) {
  if (
    auditor.auditorOption === 'person'
    && auditor.auditorFirm.organizationNumber === null
  ) {
    return {
      id: auditor.id,
      ...auditor.auditorPerson,
      organizationAddress: null,
      organizationNumber: null,
      organizationCity: null,
      organizationPostalCode: null,
      organizationName: null,
    };
  }

  if (auditor.auditorOption === 'no') {
    // If auditor option is NO, but there is an auditor
    // we instruct the backend to delete the auditor by
    // passing in `type: delete`
    // Yes, its a terrible hack indeed...
    if (auditor.auditorPerson?.socialSecurityNumber) {
      return {
        id: auditor.id,
        type: 'delete',
        ...auditor.auditorPerson,
        ...auditor.auditorFirm,
      };
    }

    return null;
  }

  if (auditor.auditorOption === 'person') {
    return {
      id: auditor.id,
      ...auditor.auditorPerson,
    };
  }

  if (auditor.auditorOption === 'company') {
    return {
      id: auditor.id,
      ...auditor.auditorPerson,
      ...auditor.auditorFirm,
    };
  }

  return {};
}

function getStaffObject(
  staff: { id: string; userId: string },
  relationType: SellOrderRelationType,
) {
  return {
    userId: staff.userId,
    id: staff.id,
    relationType,
  };
}

function getShellCompanyDeclarationObject(data: ShellCompanyDeclaration) {
  // calculation keys, it's better to keep them as null instead of 0, because 0 is also a number in accounting
  const keysToCheck = [
    'profitIncomeStatement',
    'lossIncomeStatement',
    'profitTaxAdjustments',
    'lossTaxAdjustments',
    'surplusTaxAdjustments',
    'deficitTaxAdjustments',
  ];

  type ShellCompanyDeclarationReturnType = Partial<ShellCompanyDeclaration> & {
    [key: string]: any;
  };
  const dataToReturn: ShellCompanyDeclarationReturnType = pickShellCompanyDeclaration(data);
  if (isEmpty(dataToReturn.id)) {
    delete dataToReturn.id;
  }
  keysToCheck.forEach((key) => {
    if (dataToReturn[key] === 0) {
      dataToReturn[key] = null;
    }
  });
  return dataToReturn;
}

export const selectSellUpdateRequestFactory = (id: string) =>
  createSelector(
    (state: RootState) => state.sellOrder[id],
    (state: RootState) => selectUpdateOwnersAgreementsFactory(id)(state),
    (state: RootState) => selectUpdateRepresentativesFactory(id)(state),
    (
      sellOrder,
      { peopleSellers, organizationSellers },
      representativesStore,
    ) => {
      const {
        commision,
        businessManager,
        signer,
        liquidator,
        taxOfficeContactPerson,
        adjuster,
        settelmentType,
        id,
        auditor,
        changedFiscalYear,
        company,
        shellCompanyDeclaration,
        // Omit these fields
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        physicalPeople,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        legalShareholders,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        representatives,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        bankAccount,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        clientFundsAccount,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        bank,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        deleteRequest,

        anotherBuyer,
        isAnotherBuyer,
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        newBank,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        childOrdersDetails,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        hasChildOrder,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        parentOrderId,
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        isParentCompany,

        ...rest
      } = sellOrder;
      const mapSigner = (signer: SellOrganizationSignerForm) => ({
        ...pick(['signerId', 'personId'], signer),
        person: pickSignerPerson(signer),
      });

      const mapLegalShareholder = (legal: SellOrganizationOwnerForm) => ({
        ...omitEmptyStrings(legal),
        signers: map(
          compose(
            omitEmptyStrings,
            renameProperty('signerId', 'id'),
            mapSigner,
          ),
          legal.signers,
        ),
      });

      // Build the company payload.  omitEmptyStrings removes '' but keeps null,
      // so we must also drop a null businessDescription to avoid sending it to
      // the backend where TypeORM would execute SET businessDescription = NULL.
      const companyFields = pickCompany(omitEmptyStrings(company)) as Record<string, unknown>;
      if (companyFields.businessDescription == null) {
        delete companyFields.businessDescription;
      }

      const formattedForApiRequest = {
        ...rest,
        company: {
          ...companyFields,
          id: property('id', company),
        },
        id,
        commision,
        banks: rest.banks.filter((bank) => bank.id !== NEW_BANK),
        businessManager: getStaffObject(
          businessManager,
          SellOrderRelationType.BusinessManager,
        ),
        signer: getStaffObject(signer, SellOrderRelationType.Signer),
        liquidator: getStaffObject(
          liquidator,
          SellOrderRelationType.Liquidator,
        ),
        adjuster: getStaffObject(adjuster, SellOrderRelationType.Adjuster),

        settlementType: settelmentType,
        physicalPeople: mapOmitEmptyStrings(peopleSellers),
        legalShareholders: map(mapLegalShareholder, organizationSellers),
        representatives: map(
          omit('isDraftsman'),
          mapOmitEmptyStrings(representativesStore),
        ),
        anotherBuyer: isAnotherBuyer ? anotherBuyer : null,
        auditor: getAuditorObject(auditor),
        shellCompanyDeclaration: getShellCompanyDeclarationObject(
          shellCompanyDeclaration as ShellCompanyDeclaration,
        ),
      };

      if (id === CUSTOMER_FORM_ID) {
        // @ts-ignore
        delete formattedForApiRequest.id;
      }

      if (isEmpty(formattedForApiRequest.auditor)) {
        // @ts-ignore
        delete formattedForApiRequest.auditor;
      }
      if (
        taxOfficeContactPerson
        && taxOfficeContactPerson.id !== ''
        && taxOfficeContactPerson.userId !== ''
      ) {
        (formattedForApiRequest as any).taxOfficeContactPerson = getStaffObject(
          taxOfficeContactPerson,
          SellOrderRelationType.TaxOfficeContactPerson,
        );
      }
      if (!changedFiscalYear) {
        return assign(formattedForApiRequest, {
          fiscalStartDate: null,
          fiscalEndDate: null,
        });
      }

      return formattedForApiRequest;
    },
  );
